/**
 * Product gallery v2 — Turtle PDP media module.
 *
 * Layout: left thumbs + main stage + Photos / Models / In the box tabs.
 *
 * Photos:
 * - Slot 0: selected bundle `mainImage` (switches with kit selection)
 * - Slots 1+: fixed PDP `product.media` from first image (up to 5)
 *
 * Models / In the box: lists from selected bundle catalog entry.
 *
 * Public API:
 * - `setBundleMedia({ mainImage, modelsImages, inTheBoxImages })`
 *
 * Does not modify theme.js.liquid.
 */
if (!customElements.get('product-gallery-v2')) {
  const DEBUG = false;

  class ProductGalleryV2 extends HTMLElement {
    constructor() {
      super();
      this.activeTab = 'photos';
      this.currentIndex = 0;
      this.mainImage = null;
      this.modelsImages = [];
      this.inTheBoxImages = [];
      this.fixedPhotos = [];
      this._ready = false;
    }

    connectedCallback() {
      this.fixedPhotos = this.parseFixedPhotos();
      this.thumbsEl = this.querySelector('[data-gallery-thumbs]');
      this.stageEl = this.querySelector('[data-gallery-stage]');
      this.captionEl = this.querySelector('[data-gallery-caption]');
      this.tabsEl = this.querySelector('[data-gallery-tabs]');
      this._ready = true;

      this.bindTabs();
      this.render();
      this.log('mounted', {
        fixedPhotos: this.fixedPhotos.length,
        hasMain: Boolean(this.mainImage),
      });
    }

    log(label, ...args) {
      if (!DEBUG) return;
      console.log(`[Gallery V2 · ${label}]`, ...args);
    }

    parseFixedPhotos() {
      const script = this.querySelector('[data-fixed-photos]');

      if (!script) {
        return [];
      }

      try {
        const parsed = JSON.parse(script.textContent || '[]');
        return Array.isArray(parsed) ? parsed.filter((item) => item?.src) : [];
      } catch (error) {
        this.log('fixed photos parse failed', error);
        return [];
      }
    }

    /**
     * Update bundle-driven media. Called by Complete Bundles / Build Your Own.
     * Safe to call before connectedCallback — values are kept and rendered on mount.
     * @param {{ mainImage?: object|null, modelsImages?: object[], inTheBoxImages?: object[] }} payload
     */
    setBundleMedia({ mainImage = null, modelsImages = [], inTheBoxImages = [] } = {}) {
      this.mainImage = mainImage?.src ? mainImage : null;
      this.modelsImages = Array.isArray(modelsImages)
        ? modelsImages.filter((item) => item?.src)
        : [];
      this.inTheBoxImages = Array.isArray(inTheBoxImages)
        ? inTheBoxImages.filter((item) => item?.src)
        : [];

      if (this.activeTab === 'photos') {
        this.currentIndex = 0;
      } else {
        const items = this.getTabItems(this.activeTab);

        if (!items.length) {
          this.activeTab = 'photos';
          this.currentIndex = 0;
        } else if (this.currentIndex >= items.length) {
          this.currentIndex = 0;
        }
      }

      if (!this._ready) {
        this.log('bundle media queued (not ready)', {
          hasMain: Boolean(this.mainImage),
        });
        return;
      }

      this.render();
      this.log('bundle media set', {
        hasMain: Boolean(this.mainImage),
        models: this.modelsImages.length,
        inTheBox: this.inTheBoxImages.length,
        activeTab: this.activeTab,
      });
    }

    getTabItems(tab) {
      if (tab === 'models') {
        return this.modelsImages;
      }

      if (tab === 'in_the_box') {
        return this.inTheBoxImages;
      }

      const photos = [];

      if (this.mainImage) {
        photos.push(this.mainImage);
      }

      photos.push(...this.fixedPhotos);
      return photos;
    }

    bindTabs() {
      this.tabsEl?.querySelectorAll('[data-gallery-tab]').forEach((button) => {
        button.addEventListener('click', () => {
          const tab = button.getAttribute('data-gallery-tab');

          if (!tab || tab === this.activeTab) {
            return;
          }

          const items = this.getTabItems(tab);

          if (!items.length) {
            return;
          }

          this.activeTab = tab;
          this.currentIndex = 0;
          this.render();
        });
      });
    }

    selectImage(index) {
      const items = this.getTabItems(this.activeTab);

      if (index < 0 || index >= items.length) {
        return;
      }

      this.currentIndex = index;
      this.renderThumbs();
      this.renderStage();
      this.renderCaption();
    }

    render() {
      this.renderTabs();
      this.renderThumbs();
      this.renderStage();
      this.renderCaption();
    }

    renderTabs() {
      if (!this.tabsEl) {
        return;
      }

      const visibility = {
        photos: this.getTabItems('photos').length > 0,
        models: this.modelsImages.length > 0,
        in_the_box: this.inTheBoxImages.length > 0,
      };

      this.tabsEl.querySelectorAll('[data-gallery-tab]').forEach((button) => {
        const tab = button.getAttribute('data-gallery-tab');
        const visible = Boolean(visibility[tab]);

        button.hidden = !visible;
        button.setAttribute('aria-selected', tab === this.activeTab ? 'true' : 'false');
      });
    }

    renderThumbs() {
      if (!this.thumbsEl) {
        return;
      }

      const items = this.getTabItems(this.activeTab);

      this.thumbsEl.innerHTML =
        items
          .map((item, index) => {
            const current = index === this.currentIndex;
            const alt = this.escapeAttr(item.alt || '');

            return `<button type="button" class="product-gallery-v2__thumb" data-gallery-thumb-index="${index}" aria-current="${
              current ? 'true' : 'false'
            }" aria-label="Image ${index + 1}">
            <img src="${this.escapeAttr(item.src)}" alt="${alt}" width="${
              item.width || ''
            }" height="${item.height || ''}" loading="lazy">
          </button>`;
          })
          .join('') + '<span class="product-gallery-v2__thumb-spacer" aria-hidden="true"></span>';

      this.thumbsEl.querySelectorAll('[data-gallery-thumb-index]').forEach((button) => {
        button.addEventListener('click', () => {
          const index = Number.parseInt(button.getAttribute('data-gallery-thumb-index') || '', 10);
          this.selectImage(index);
        });
      });

      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.scrollActiveThumbIntoView(prefersReducedMotion ? 'auto' : 'smooth');
    }

    scrollActiveThumbIntoView(behavior = 'smooth') {
      if (!this.thumbsEl) {
        return;
      }

      const thumb = this.thumbsEl.querySelector('[aria-current="true"]');

      if (!thumb) {
        return;
      }

      const container = this.thumbsEl;
      const targetScrollTop =
        thumb.offsetTop - container.clientHeight / 2 + thumb.offsetHeight / 2;

      container.scrollTo({
        top: Math.max(0, targetScrollTop),
        behavior,
      });
    }

    renderStage() {
      if (!this.stageEl) {
        return;
      }

      const items = this.getTabItems(this.activeTab);
      const item = items[this.currentIndex] || items[0];

      if (!item) {
        this.stageEl.innerHTML = '';
        return;
      }

      this.stageEl.innerHTML = `<img src="${this.escapeAttr(item.src)}" alt="${this.escapeAttr(
        item.alt || ''
      )}" width="${item.width || ''}" height="${item.height || ''}" loading="eager">`;
    }

    renderCaption() {
      if (!this.captionEl) {
        return;
      }

      const items = this.getTabItems(this.activeTab);
      const item = items[this.currentIndex] || items[0];
      const caption = (item?.alt || '').trim();

      this.captionEl.textContent = caption;
    }

    escapeAttr(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    }
  }

  customElements.define('product-gallery-v2', ProductGalleryV2);
}
